import React, { useRef, useEffect, useState, useCallback } from 'react';
import { useLanguage } from '@/context/LanguageContext';

const phrases = {
  es: {
    white: 'No somos las ideas que tenemos,',
    red: 'sino las que ejecutamos',
  },
  en: {
    white: 'We are not the ideas we have,',
    red: 'but the ones we execute',
  },
};

const navLinks = {
  es: [
    { label: 'Misión', id: 'mission' },
    { label: 'Proceso', id: 'workflow' },
    { label: 'Servicios', id: 'services' },
    { label: 'FAQ', id: 'faq' },
    { label: 'Contacto', id: 'contact' },
  ],
  en: [
    { label: 'Mission', id: 'mission' },
    { label: 'How We Work', id: 'workflow' },
    { label: 'Services', id: 'services' },
    { label: 'FAQ', id: 'faq' },
    { label: 'Contact', id: 'contact' },
  ],
};

export default function Footer() {
  const { lang, t } = useLanguage();
  const sectionRef = useRef(null);
  const [progress, setProgress] = useState(0);

  const update = useCallback(() => {
    if (!sectionRef.current) return;
    const rect = sectionRef.current.getBoundingClientRect();
    const viewH = window.innerHeight;
    const p = Math.max(0, Math.min(1, (viewH - rect.top) / (viewH + rect.height * 0.3)));
    setProgress(p);
  }, []);

  useEffect(() => {
    const lenis = window.__lenis;
    if (lenis) {
      lenis.on('scroll', update);
      update();
      return () => lenis.off('scroll', update);
    }
    const handler = () => requestAnimationFrame(update);
    window.addEventListener('scroll', handler, { passive: true });
    update();
    return () => window.removeEventListener('scroll', handler);
  }, [update]);

  const whiteWords = phrases[lang].white.split(' ');
  const redWords = phrases[lang].red.split(' ');
  const allWords = [...whiteWords, ...redWords];
  const total = allWords.length;

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <footer
      ref={sectionRef}
      data-testid="footer"
      className="relative px-6 md:px-12 lg:px-24 border-t border-white/10"
      style={{ background: '#050505' }}
    >
      {/* Top section: phrase left, links right */}
      <div className="flex flex-col md:flex-row justify-between gap-12 pt-12 md:pt-16 pb-8">
        {/* Left: animated phrase */}
        <div className="max-w-2xl">
          <p
            aria-hidden="true"
            className="font-heading font-bold tracking-tight select-none"
            style={{ lineHeight: 1.2, fontSize: 'clamp(1.5rem, 2.8vw, 3rem)' }}
            data-testid="footer-phrase"
          >
            {/* Line 1: white text - forced single line */}
            <span className="block whitespace-nowrap">
              {phrases[lang].white.split(' ').map((word, i) => {
                const wp = Math.max(0, Math.min(1, (progress - (i / total) * 0.5) / 0.3));
                return (
                  <span key={`${lang}-w-${i}`} className="inline mr-[0.25em]" style={{ color: '#ffffff', opacity: 0.1 + wp * 0.9 }}>
                    {word}
                  </span>
                );
              })}
            </span>
            {/* Line 2: red text - forced single line */}
            <span className="block whitespace-nowrap">
              {phrases[lang].red.split(' ').map((word, i) => {
                const idx = whiteWords.length + i;
                const wp = Math.max(0, Math.min(1, (progress - (idx / total) * 0.5) / 0.3));
                return (
                  <span key={`${lang}-r-${i}`} className="inline mr-[0.25em]" style={{ color: '#e14141', opacity: 0.1 + wp * 0.9 }}>
                    {word}
                  </span>
                );
              })}
            </span>
          </p>
        </div>

        {/* Right: nav + socials columns */}
        <div className="flex gap-16 md:gap-20">
          {/* Navigate */}
          <div>
            <span className="font-mono text-[10px] tracking-[0.2em] text-zinc-500 uppercase block mb-4">
              {lang === 'es' ? 'Navegar' : 'Navigate'}
            </span>
            <ul className="space-y-2.5">
              {navLinks[lang].map((link) => (
                <li key={link.id}>
                  <button
                    onClick={() => scrollTo(link.id)}
                    data-testid={`footer-nav-${link.id}`}
                    className="font-body text-sm text-zinc-400 hover:text-white transition-colors duration-300"
                  >
                    {link.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Connect */}
          <div>
            <span className="font-mono text-[10px] tracking-[0.2em] text-zinc-500 uppercase block mb-4">
              {lang === 'es' ? 'Conectar' : 'Connect'}
            </span>
            <ul className="space-y-2.5">
              <li>
                <a href="https://www.instagram.com/firststudio.cc/" target="_blank" rel="noopener noreferrer" data-testid="footer-social-instagram" className="font-body text-sm text-zinc-400 hover:text-white transition-colors duration-300">
                  Instagram
                </a>
              </li>
              <li>
                <a href="https://www.linkedin.com/company/firststudio/" target="_blank" rel="noopener noreferrer" data-testid="footer-social-linkedin" className="font-body text-sm text-zinc-400 hover:text-white transition-colors duration-300">
                  LinkedIn
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Center: large logo, partially hidden at bottom */}
      <div className="relative overflow-hidden" style={{ height: 'clamp(200px, 35vw, 450px)' }}>
        {/* Gradient fade from bottom */}
        <div className="absolute bottom-0 left-0 right-0 h-1/2 z-10 pointer-events-none" style={{ background: 'linear-gradient(to top, #050505, transparent)' }} />
        <img
          src="/LOGO BLANCO FIRST.avif"
          alt="FirstStudioLogo"
          width="1152"
          height="647"
          className="absolute left-1/2 -translate-x-1/2 top-[5%] w-[60%] md:w-[55%] lg:w-[45%] max-w-[700px] h-auto opacity-40"
          data-testid="footer-logo"
        />
      </div>

      {/* Bottom bar */}
        <div className="border-t border-white/10 py-6 flex flex-col md:flex-row items-center justify-between gap-4">
          
          <p className="font-mono text-[10px] tracking-wider text-zinc-600 uppercase text-center md:text-left" data-testid="footer-copyright">
            &copy; {new Date().getFullYear()} First Studio. {t.footer.rights}
          </p>

          <div className="flex flex-wrap justify-center gap-6 font-mono text-[10px] tracking-wider text-zinc-600 uppercase">
            <a href="/aviso-legal" className="hover:text-white transition-colors duration-300">
              {t.footer.legalNotice}
            </a>
            <a href="/politica-privacidad" className="hover:text-white transition-colors duration-300">
              {t.footer.privacyPolicy}
            </a>
            <a href="/politica-cookies" className="hover:text-white transition-colors duration-300">
              {t.footer.cookiePolicy}
            </a>
          </div>

        </div>
    </footer>
  );
}
